/**
 * Virtual Wall of Hope — Figma composition with measured 24px gutters.
 * Places cards into the original column structure; tops follow content height.
 */

const DESKTOP_MQ = '(min-width: 1200px)';
const GAP = 24;

/** Column slots matching the original desktop composition */
const SLOTS = [
	{ key: 'clara', col: 0, stagger: 0 },
	{ key: 'sophia', col: 1, stagger: 14 },
	{ key: 'courtney', col: 2, stagger: 6 },
	{ key: 'broken', col: 3, stagger: 0 },
	{ key: 'emily', col: 0, stagger: 0 },
	{ key: 'feature', col: 1, span: 2, stagger: 0 },
	{ key: 'andrew', col: 3, stagger: 0 },
	{ key: 'love', col: 0, stagger: 0 },
	{ key: 'sister', col: 1, stagger: 0 },
	{ key: 'listen', col: 2, stagger: 8 },
	{ key: 'feature-tall', col: 3, stagger: 0 },
	{ key: 'remember', col: 0, stagger: 0 },
	{ key: 'gentle', col: 1, stagger: 12 },
	{ key: 'meera', col: 2, stagger: 0 },
	{ key: 'arjun', col: 3, stagger: 6 },
	{ key: 'nisha', col: 0, stagger: 0 },
	{ key: 'ananya', col: 1, stagger: 0 },
	{ key: 'lakshmi', col: 2, stagger: 10 },
	{ key: 'fatima', col: 3, stagger: 0 },
	{ key: 'rohan', col: 0, stagger: 4 },
	{ key: 'sana', col: 1, stagger: 0 },
];

/** Brand swatches from the reference wall (lined = white notebook paper) */
const COLORS = {
	magenta: { key: 'magenta', bg: '#BF4C96', fg: '#ffffff' },
	teal: { key: 'teal', bg: '#18A191', fg: '#ffffff' },
	yellow: { key: 'yellow', bg: '#FFD100', fg: '#000000' },
	blue: { key: 'blue', bg: '#10A0D1', fg: '#ffffff' },
	plum: { key: 'plum', bg: '#5E2355', fg: '#ffffff' },
	lined: { key: 'lined', bg: '#ffffff', fg: '#2f3238' },
};

const PALETTE = [ 'magenta', 'teal', 'yellow', 'blue', 'lined' ];

/**
 * Exact placement matching the reference image:
 * colored cards + white lined-paper cards.
 */
const SLOT_COLORS = {
	clara: 'magenta',
	sophia: 'teal',
	courtney: 'yellow',
	broken: 'blue',
	emily: 'blue',
	feature: 'plum',
	andrew: 'teal',
	love: 'yellow',
	sister: 'teal',
	listen: 'yellow',
	'feature-tall': 'magenta',
	remember: 'plum',
	gentle: 'blue',
	meera: 'yellow',
	arjun: 'plum',
	nisha: 'teal',
	ananya: 'magenta',
	lakshmi: 'teal',
	fatima: 'yellow',
	rohan: 'lined',
	sana: 'lined',
};

/** Stable hash so the same name always gets the same tilt. */
function hashName( name ) {
	let hash = 0;

	for ( let i = 0; i < name.length; i++ ) {
		hash = ( ( hash << 5 ) - hash ) + name.charCodeAt( i );
		hash |= 0;
	}

	return Math.abs( hash );
}

function getNoteKey( note ) {
	const mod = [ ...note.classList ].find( ( c ) =>
		/^bts-hope-paper--(?!tape)/.test( c )
	);

	return mod ? mod.replace( 'bts-hope-paper--', '' ) : '';
}

function getNoteName( note ) {
	const nameEl = note.querySelector( '.bts-hope-paper__name' );
	return ( nameEl?.textContent || '' ).trim().toLowerCase() || 'anonymous';
}

/**
 * Tilt from author name; color from reference layout map (by card slot).
 */
function applyNameStyles( notes ) {
	notes.forEach( ( note, index ) => {
		const name = getNoteName( note );
		const hash = hashName( name );
		const key = getNoteKey( note );

		const magnitude = 0.8 + ( hash % 11 ) / 10; // 0.8 … 1.8
		const sign = hash % 2 === 0 ? 1 : -1;
		const degrees = Math.round( sign * magnitude * 10 ) / 10;

		const colorName = note.dataset.hopeColor || SLOT_COLORS[ key ] || PALETTE[ index % PALETTE.length ];
		const color = COLORS[ colorName ] || COLORS.magenta;

		note.style.setProperty( '--hope-tilt', `${ degrees }deg` );
		note.style.setProperty( '--hope-card-bg', color.bg );
		note.style.setProperty( '--hope-card-fg', color.fg );
		note.dataset.hopeColor = color.key;
		note.classList.toggle( 'bts-hope-paper--lined', color.key === 'lined' );
	} );
}

function updateToggles( notes ) {
	notes.forEach( ( note ) => {
		const text = note.querySelector( '.bts-hope-paper__text' );
		const toggle = note.querySelector( '.bts-hope-paper__toggle' );

		if ( ! text || ! toggle || note.classList.contains( 'is-expanded' ) ) {
			return;
		}

		toggle.hidden = text.scrollHeight <= text.clientHeight + 1;
	} );
}

function clearTops( notes ) {
	notes.forEach( ( note ) => {
		note.style.removeProperty( 'top' );
	} );
}

/**
 * Set vertical positions from measured heights so gutters stay ~24px,
 * while left/width stay in CSS (Figma column widths).
 */
function layoutDesktop( wall, notes ) {
	if ( ! window.matchMedia( DESKTOP_MQ ).matches ) {
		clearTops( notes );
		wall.classList.remove( 'is-laid-out' );
		wall.style.minHeight = '';
		return;
	}

	const byKey = {};

	notes.forEach( ( note ) => {
		const mod = [ ...note.classList ].find( ( c ) =>
			/^bts-hope-paper--(?!tape)/.test( c )
		);

		if ( ! mod ) {
			return;
		}

		const key = mod.replace( 'bts-hope-paper--', '' );
		byKey[ key ] = note;
	} );

	// Enable absolute column widths before measuring heights.
	wall.classList.add( 'is-laid-out' );
	clearTops( notes );

	const colBottom = [ 0, 0, 0, 0 ];
	const known = {};

	SLOTS.forEach( ( slot ) => {
		known[ slot.key ] = true;
		const note = byKey[ slot.key ];

		if ( ! note ) {
			return;
		}

		const span = slot.span || 1;
		let top = 0;

		for ( let c = slot.col; c < slot.col + span; c++ ) {
			top = Math.max( top, colBottom[ c ] );
		}

		top += slot.stagger || 0;
		note.style.top = `${ top }px`;

		const height = note.offsetHeight;
		const next = top + height + GAP;

		for ( let c = slot.col; c < slot.col + span; c++ ) {
			colBottom[ c ] = next;
		}
	} );

	notes.forEach( ( note ) => {
		const key = getNoteKey( note );

		if ( known[ key ] ) {
			return;
		}

		const col = parseInt( note.getAttribute( 'data-hope-col' ), 10 );

		if ( ! ( col >= 0 && col <= 3 ) ) {
			return;
		}

		const top = colBottom[ col ];
		note.style.top = `${ top }px`;
		colBottom[ col ] = top + note.offsetHeight + GAP;
	} );

	wall.style.minHeight = `${ Math.max( ...colBottom ) - GAP + 8 }px`;
}

const PREFERS_REDUCED = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/** Stories Coming Together — one stack, all cards spread at once */
const ANIM = {
	stackEnter: 650,
	pause: 250,
	separateMin: 1000,
	separateMax: 1400,
	tape: 225,
	ease: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
};

function delay( ms ) {
	return new Promise( ( resolve ) => {
		window.setTimeout( resolve, ms );
	} );
}

function clearAnimClasses( note ) {
	note.classList.remove(
		'is-hope-pending',
		'is-hope-in-stack',
		'is-hope-separating',
		'is-hope-taping',
		'is-hope-done'
	);
}

function cleanupLegacyRoll( note ) {
	note.querySelectorAll( '.bts-hope-paper__roll, .bts-hope-paper__curl' ).forEach( ( el ) => {
		el.remove();
	} );
	note.removeAttribute( 'data-hope-roll' );
	note.style.removeProperty( '--hope-unroll-ms' );
}

function clearMotionInline( note ) {
	note.style.removeProperty( 'transform' );
	note.style.removeProperty( 'opacity' );
	note.style.removeProperty( 'visibility' );
	note.style.removeProperty( 'z-index' );
	note.style.removeProperty( '--hope-tape-op' );
}

function revealCardImmediate( note ) {
	note.getAnimations( { subtree: false } ).forEach( ( anim ) => anim.cancel() );
	clearAnimClasses( note );
	clearMotionInline( note );
	note.classList.add( 'is-hope-done' );
	note.dataset.hopeAnim = 'done';
	note.style.setProperty( '--hope-tape-op', '1' );
}

function orderedNotes( notes ) {
	const byKey = {};

	notes.forEach( ( note ) => {
		const key = getNoteKey( note );

		if ( key ) {
			byKey[ key ] = note;
		}
	} );

	const ordered = [];

	SLOTS.forEach( ( slot ) => {
		if ( byKey[ slot.key ] ) {
			ordered.push( byKey[ slot.key ] );
		}
	} );

	notes.forEach( ( note ) => {
		if ( ! ordered.includes( note ) ) {
			ordered.push( note );
		}
	} );

	return ordered;
}

function readTiltDeg( note ) {
	const raw = getComputedStyle( note ).getPropertyValue( '--hope-tilt' ).trim();
	const n = parseFloat( raw );

	return Number.isFinite( n ) ? n : 0;
}

/**
 * Measure final masonry seats with transforms forced off so
 * stack offsets are accurate and cards stay invisible.
 */
function measureSeatCenters( wall, notes ) {
	wall.classList.add( 'is-hope-measuring' );
	void wall.offsetWidth;

	const wallRect = wall.getBoundingClientRect();
	const seats = new Map();

	notes.forEach( ( note ) => {
		const rect = note.getBoundingClientRect();

		seats.set( note, {
			cx: rect.left - wallRect.left + rect.width / 2,
			cy: rect.top - wallRect.top + rect.height / 2,
		} );
	} );

	wall.classList.remove( 'is-hope-measuring' );

	return seats;
}

function buildStackPlan( wall, notes, seats ) {
	const stackCX = wall.offsetWidth / 2;
	const visibleH = Math.min(
		wall.offsetHeight || wall.getBoundingClientRect().height,
		window.innerHeight * 0.72
	);
	const stackCY = Math.max( 140, visibleH * 0.4 );
	const rise = window.innerWidth < 768 ? 40 : 90;
	const jitterScale = window.innerWidth < 768 ? 5 : 7;

	const draft = notes.map( ( note, index ) => {
		const seat = seats.get( note ) || { cx: stackCX, cy: stackCY };
		const jitterX = ( ( index % 5 ) - 2 ) * jitterScale;
		const jitterY = ( ( index % 4 ) - 1.5 ) * ( jitterScale * 0.75 );
		const stackX = stackCX - seat.cx + jitterX;
		const stackY = stackCY - seat.cy + jitterY;
		const travel = Math.hypot( stackX, stackY );

		return {
			note,
			index,
			stackX,
			stackY,
			enterY: stackY - rise,
			stackRot: ( ( index % 5 ) - 2 ) * 2.2,
			tilt: readTiltDeg( note ),
			travel,
			z: 20 + index,
		};
	} );

	const maxTravel = Math.max( ...draft.map( ( item ) => item.travel ), 1 );

	return draft.map( ( item ) => ( {
		...item,
		// Longer paths take a bit longer; all starts are simultaneous
		separateMs: Math.round(
			ANIM.separateMin +
				( item.travel / maxTravel ) * ( ANIM.separateMax - ANIM.separateMin )
		),
	} ) );
}

function runWaapi( note, keyframes, options ) {
	const anim = note.animate( keyframes, {
		...options,
		fill: 'forwards',
	} );

	return anim.finished.then( () => anim );
}

/**
 * Single timeline for ALL cards:
 * one stack arrives → pause → every card spreads at once → tape.
 */
async function animateAllCardsFromStack( wall, notes, seats ) {
	const active = notes.filter( ( note ) => note.dataset.hopeAnim === 'pending' );

	if ( ! active.length ) {
		return;
	}

	const plan = buildStackPlan( wall, active, seats );

	// Assemble full stack above center (all together)
	plan.forEach( ( item ) => {
		const { note } = item;

		note.dataset.hopeAnim = 'running';
		cleanupLegacyRoll( note );
		note.getAnimations( { subtree: false } ).forEach( ( anim ) => anim.cancel() );
		clearAnimClasses( note );
		note.classList.add( 'is-hope-in-stack' );
		note.style.visibility = 'visible';
		note.style.opacity = '1';
		note.style.zIndex = String( item.z );
		note.style.setProperty( '--hope-tape-op', '0' );
		note.style.transform =
			`translate(${ item.stackX }px, ${ item.enterY }px) rotate(${ item.stackRot }deg)`;
	} );

	void wall.offsetWidth;

	// Step 1 — whole stack descends together
	await Promise.all(
		plan.map( ( item ) =>
			runWaapi(
				item.note,
				[
					{
						transform:
							`translate(${ item.stackX }px, ${ item.enterY }px) rotate(${ item.stackRot }deg)`,
						opacity: 1,
					},
					{
						transform:
							`translate(${ item.stackX }px, ${ item.stackY }px) rotate(${ item.stackRot }deg)`,
						opacity: 1,
					},
				],
				{ duration: ANIM.stackEnter, easing: ANIM.ease }
			)
		)
	);

	plan.forEach( ( item ) => {
		item.note.style.transform =
			`translate(${ item.stackX }px, ${ item.stackY }px) rotate(${ item.stackRot }deg)`;
	} );

	await delay( ANIM.pause );

	// Step 2 — prepare every card, then start ALL spread animations in one sync tick
	plan.forEach( ( item ) => {
		item.note.classList.remove( 'is-hope-in-stack' );
		item.note.classList.add( 'is-hope-separating' );
		item.note.style.setProperty( '--hope-tape-op', '0' );
	} );

	const spreadJobs = plan.map( ( item ) => {
		const { note } = item;
		const midX = item.stackX * 0.34;
		const midY = item.stackY * 0.3 - ( window.innerWidth < 768 ? 8 : 12 );
		const midRot = item.stackRot * 0.35 + item.tilt * 0.65;

		return runWaapi(
			note,
			[
				{
					transform:
						`translate(${ item.stackX }px, ${ item.stackY }px) rotate(${ item.stackRot }deg)`,
				},
				{
					transform:
						`translate(${ midX }px, ${ midY }px) rotate(${ midRot }deg)`,
					offset: 0.55,
				},
				{
					transform: `translate(0px, 0px) rotate(${ item.tilt }deg)`,
				},
			],
			{ duration: item.separateMs, easing: ANIM.ease }
		).then( async () => {
			note.style.transform = `translate(0px, 0px) rotate(${ item.tilt }deg)`;
			note.classList.remove( 'is-hope-separating' );
			note.classList.add( 'is-hope-taping' );

			// Step 3 — tape after this card arrives
			note.style.setProperty( '--hope-tape-op', '0' );
			void note.offsetWidth;
			note.style.setProperty( '--hope-tape-op', '1' );
			await delay( ANIM.tape );
			revealCardImmediate( note );
		} );
	} );

	await Promise.all( spreadJobs );
}

function isInViewport( el ) {
	const rect = el.getBoundingClientRect();
	const vh = window.innerHeight || document.documentElement.clientHeight;
	const vw = window.innerWidth || document.documentElement.clientWidth;

	return (
		rect.width > 0 &&
		rect.height > 0 &&
		rect.bottom > 0 &&
		rect.right > 0 &&
		rect.top < vh &&
		rect.left < vw
	);
}

/**
 * One stack of every story card → simultaneous outward spread.
 */
function initStoriesTogetherAnimation( wall, notes ) {
	if ( PREFERS_REDUCED() ) {
		wall.classList.remove( 'is-hope-animating' );
		notes.forEach( revealCardImmediate );
		return;
	}

	wall.classList.add( 'is-hope-animating' );

	notes.forEach( ( note ) => {
		cleanupLegacyRoll( note );
		note.getAnimations( { subtree: false } ).forEach( ( anim ) => anim.cancel() );
		clearAnimClasses( note );
		clearMotionInline( note );
		note.classList.add( 'is-hope-pending' );
		note.dataset.hopeAnim = 'pending';
		note.style.setProperty( '--hope-tape-op', '0' );
		note.removeAttribute( 'data-hope-group' );
	} );

	const ordered = orderedNotes( notes );
	const seats = measureSeatCenters( wall, ordered );
	let started = false;

	const run = async () => {
		if ( started ) {
			return;
		}

		started = true;

		try {
			await animateAllCardsFromStack( wall, ordered, seats );
		} catch ( error ) {
			notes.forEach( revealCardImmediate );
		}
	};

	if ( 'IntersectionObserver' in window ) {
		const wallObserver = new IntersectionObserver(
			( entries ) => {
				entries.forEach( ( entry ) => {
					if ( entry.isIntersecting ) {
						run();
						wallObserver.disconnect();
					}
				} );
			},
			{ root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
		);

		wallObserver.observe( wall );
	} else {
		run();
	}

	window.requestAnimationFrame( () => {
		if ( isInViewport( wall ) ) {
			run();
		}
	} );

	window.setTimeout( () => {
		if ( ! started ) {
			run();
		}
	}, 900 );

	window.setTimeout( () => {
		notes.forEach( ( note ) => {
			if ( note.dataset.hopeAnim !== 'done' ) {
				revealCardImmediate( note );
			}
		} );
	}, 30000 );
}

function initBoard( section ) {
	const wall = section.querySelector( '.bts-hope-board__wall' );

	if ( ! wall ) {
		return;
	}

	const notes = Array.from( wall.querySelectorAll( '.bts-hope-paper' ) );
	let animStarted = false;

	// Hide cards before first paint of the board animation cycle
	if ( ! PREFERS_REDUCED() ) {
		wall.classList.add( 'is-hope-animating' );
		notes.forEach( ( note ) => {
			note.classList.add( 'is-hope-pending' );
			note.style.setProperty( '--hope-tape-op', '0' );
		} );
	}

	applyNameStyles( notes );

	const refresh = () => {
		applyNameStyles( notes );
		updateToggles( notes );
		layoutDesktop( wall, notes );
		updateToggles( notes );
		layoutDesktop( wall, notes );
	};

	wall.addEventListener( 'click', ( event ) => {
		const toggle = event.target.closest( '.bts-hope-paper__toggle' );

		if ( ! toggle ) {
			return;
		}

		const note = toggle.closest( '.bts-hope-paper' );
		const expanded = note.classList.toggle( 'is-expanded' );

		toggle.setAttribute( 'aria-expanded', String( expanded ) );
		toggle.textContent = expanded ? toggle.dataset.lessLabel : toggle.dataset.moreLabel;
		refresh();
	} );

	let resizeTimer;
	window.addEventListener( 'resize', () => {
		window.clearTimeout( resizeTimer );
		resizeTimer = window.setTimeout( () => {
			refresh();
		}, 120 );
	} );

	refresh();

	const startAnim = () => {
		if ( animStarted ) {
			return;
		}

		animStarted = true;
		refresh();
		initStoriesTogetherAnimation( wall, notes );
	};

	window.requestAnimationFrame( () => {
		window.requestAnimationFrame( startAnim );
	} );

	window.setTimeout( startAnim, 120 );

	window.addEventListener( 'load', startAnim, { once: true } );
}

export default function initHopeBoard() {
	document.querySelectorAll( '.bts-hope-board:not(.is-opposite-slide-fixed)' ).forEach( initBoard );
}
